/**
 * Site Master Dataset Management
 *
 * Manage CAPEX pricing data for a specific site.
 * Can sync from company master to pull new/updated items.
 * Edits here are marked as user-modified and won't be overwritten by syncs.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Database, Download, Upload, ArrowLeft, RefreshCw, Plus, Edit2, Save, X, Trash2,
  Search, ChevronDown, ChevronRight, RefreshCcw, FileUp
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SEU_COLORS = {
  'A. ELECTRICAL': { bg: 'bg-blue-50', badge: 'bg-blue-600' },
  'B. THERMAL': { bg: 'bg-orange-50', badge: 'bg-orange-600' },
  'C. COOLING': { bg: 'bg-cyan-50', badge: 'bg-cyan-600' },
  'D. WATER': { bg: 'bg-teal-50', badge: 'bg-teal-600' },
  'E. WASTE': { bg: 'bg-amber-50', badge: 'bg-amber-600' },
  'F. PROCESS': { bg: 'bg-purple-50', badge: 'bg-purple-600' },
};

const getSeuColor = (seuType) => {
  for (const [key, val] of Object.entries(SEU_COLORS)) {
    if (seuType?.toUpperCase().startsWith(key)) return val;
  }
  return { bg: 'bg-gray-50', badge: 'bg-gray-600' };
};

const SiteMasterDataset = ({ user, onLogout }) => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const { availableSites } = useSite();
  const fileInputRef = useRef(null);

  // Force-refresh overwrites site customizations — admin-family only (UI gate;
  // the backend enforces the same on ?force=true).
  const ADMIN_ROLES = ['admin', 'Admin', 'super_admin', 'Super Admin', 'tenant_admin', 'Tenant Admin'];
  const currentRole = (user && user.role) || JSON.parse(localStorage.getItem('user') || '{}').role;
  const isAdmin = ADMIN_ROLES.includes(currentRole);

  const [siteName, setSiteName] = useState('');
  const [items, setItems] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSEUs, setExpandedSEUs] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [newItem, setNewItem] = useState({
    seu_type: '', opportunity_name: '', capex_item_name: '',
    technical_spec: '', capacity_basis: 'kW',
    unit_cost_low: 0, unit_cost_high: 0,
    currency: 'INR', country: 'India',
    typical_makes: '', lifecycle_years: 10,
  });
  const [stats, setStats] = useState({ total: 0, opportunities: 0, modified: 0 });

  useEffect(() => { fetchData(); }, [siteId]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const baseUrl = `${API}/api/opportunity-v2/sites/${siteId}/master-dataset`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dataRes, siteRes] = await Promise.all([
        axios.get(baseUrl, { headers }),
        axios.get(`${API}/org/sites`, { headers }).catch(() => ({ data: [] })),
      ]);
      setItems(dataRes.data.items || []);
      setGroupedData(dataRes.data.grouped_by_seu || {});

      const allItems = dataRes.data.items || [];
      setStats({
        total: allItems.length,
        opportunities: new Set(allItems.map(i => i.opportunity_name)).size,
        modified: allItems.filter(i => i.is_user_modified).length,
      });

      // Resolve site name — try context first, then API response
      const ctxSite = (availableSites || []).find(s => s.site_id === siteId);
      if (ctxSite) {
        setSiteName(ctxSite.site_name || ctxSite.name || siteId);
      } else {
        const sites = siteRes.data || [];
        const site = sites.find(s => (s.site_id || s.id) === siteId);
        setSiteName(site?.site_name || site?.name || siteId);
      }

      if (dataRes.data.seu_types?.length > 0 && expandedSEUs.length === 0) {
        setExpandedSEUs([dataRes.data.seu_types[0]]);
      }
    } catch (err) {
      showError('Failed to load site dataset');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (item) => {
    setSaving(true);
    try {
      await axios.put(`${baseUrl}/${item.capex_item_id}`, {
        study_capex_id: item.capex_item_id,
        unit_cost_low: editData.unit_cost_low,
        unit_cost_high: editData.unit_cost_high,
        technical_spec: editData.technical_spec,
        lifecycle_years: editData.lifecycle_years,
      }, { headers });
      showSuccess('Item updated');
      setEditingRow(null);
      fetchData();
    } catch (err) {
      showError('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const validateNewItem = () => {
    const errors = {};
    if (!newItem.seu_type) errors.seu_type = 'Required';
    if (!newItem.opportunity_name?.trim()) errors.opportunity_name = 'Required';
    if (!newItem.capex_item_name?.trim()) errors.capex_item_name = 'Required';
    if (newItem.unit_cost_low < 0) errors.unit_cost_low = 'Cannot be negative';
    if (newItem.unit_cost_high < 0) errors.unit_cost_high = 'Cannot be negative';
    if (newItem.unit_cost_high > 0 && newItem.unit_cost_low > newItem.unit_cost_high) errors.unit_cost_low = 'Cannot exceed Cost High';
    if (!newItem.lifecycle_years || newItem.lifecycle_years < 1) errors.lifecycle_years = 'Min 1 year';
    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateNewItem()) return;
    try {
      const makes = newItem.typical_makes ? newItem.typical_makes.split(',').map(m => m.trim()).filter(Boolean) : [];
      await axios.post(baseUrl, { ...newItem, typical_makes: makes }, { headers });
      showSuccess('Item added');
      setShowAddModal(false);
      setAddErrors({});
      setNewItem({ seu_type: '', opportunity_name: '', capex_item_name: '', technical_spec: '', capacity_basis: 'kW', unit_cost_low: 0, unit_cost_high: 0, currency: 'INR', country: 'India', typical_makes: '', lifecycle_years: 10 });
      fetchData();
    } catch (err) {
      showError('Failed to add');
    }
  };

  const handleDelete = async (capexItemId) => {
    const ok = await confirm('Delete Item', 'Remove this item from the site dataset?', { destructive: true });
    if (!ok) return;
    try {
      await axios.delete(`${baseUrl}/${capexItemId}`, { headers });
      showSuccess('Deleted');
      fetchData();
    } catch (err) {
      showError('Failed to delete');
    }
  };

  const handleSyncFromCompany = async () => {
    const ok = await confirm('Sync from Company Master', 'This will add new items and update unmodified items from the company master. Your customized items will not be changed.');
    if (!ok) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${baseUrl}/sync-from-company`, {}, { headers });
      showSuccess(`Sync complete: ${res.data.added} added, ${res.data.updated} updated`);
      fetchData();
    } catch (err) {
      showError('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleForceSyncFromCompany = async () => {
    const ok = await confirm(
      'Force Refresh from Company Master',
      'This will OVERWRITE every item at this site with the company master values — including any site-level customizations. This cannot be undone. Continue?',
      { destructive: true, confirmLabel: 'Overwrite & Refresh' }
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${baseUrl}/sync-from-company?force=true`, {}, { headers });
      showSuccess(`Force refresh complete: ${res.data.added} added, ${res.data.updated} updated`);
      fetchData();
    } catch (err) {
      showError('Force refresh failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${baseUrl}/export`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `site_master_dataset_${siteName.replace(/\s/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${baseUrl}/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      showSuccess(`Import complete: ${res.data.inserted_count} added, ${res.data.updated_count} updated`);
      fetchData();
    } catch (err) {
      showError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const toggleSEU = (seuType) => {
    setExpandedSEUs(prev => prev.includes(seuType) ? prev.filter(s => s !== seuType) : [...prev, seuType]);
  };

  const filteredSEUTypes = Object.keys(groupedData).filter(seu => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    if (seu.toLowerCase().includes(term)) return true;
    const opps = groupedData[seu]?.opportunities || {};
    return Object.keys(opps).some(o => o.toLowerCase().includes(term)) ||
      Object.values(opps).flat().some(i => i.capex_item_name?.toLowerCase().includes(term));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8 flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/ers/sites')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <Database className="w-7 h-7 text-amber-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Site Master Dataset</h1>
                <p className="text-sm text-gray-500">{siteName}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0 flex-wrap">
            <button onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#1B5C94] text-white rounded-lg text-sm font-semibold hover:bg-[#144a78]">
              <Plus className="w-4 h-4" /> Add Row
            </button>
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export
            </button>
            <label className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 cursor-pointer">
              <FileUp className="w-4 h-4" /> {importing ? 'Importing...' : 'Import CSV'}
              <input ref={importInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" />
            </label>
            <button onClick={handleSyncFromCompany} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
              <RefreshCcw className="w-4 h-4" /> {syncing ? 'Syncing...' : 'Sync from Company'}
            </button>
            {isAdmin && (
              <button onClick={handleForceSyncFromCompany} disabled={syncing}
                title="Overwrite ALL site items (including customizations) with company master values"
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                <RefreshCcw className="w-4 h-4" /> {syncing ? 'Refreshing...' : 'Force Refresh'}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-amber-500">
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-xs text-gray-500">Total Items</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
            <div className="text-2xl font-bold text-gray-800">{stats.opportunities}</div>
            <div className="text-xs text-gray-500">Unique Opportunities</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
            <div className="text-2xl font-bold text-gray-800">{stats.modified}</div>
            <div className="text-xs text-gray-500">Customized Items</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
        </div>

        {/* Data */}
        {filteredSEUTypes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
            No items. Use "Sync from Company" to pull the company master dataset.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSEUTypes.map(seuType => {
              const seuData = groupedData[seuType];
              const color = getSeuColor(seuType);
              const isExpanded = expandedSEUs.includes(seuType);
              return (
                <div key={seuType} className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button onClick={() => toggleSEU(seuType)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left ${color.bg} rounded-t-xl`}>
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${color.badge}`}>{seuData.count}</span>
                      <span className="font-semibold text-sm text-gray-800">{seuType}</span>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Opportunity</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">CAPEX Item</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Cost Low</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Cost High</th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-600">Life</th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-600">Modified</th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(seuData.opportunities || {}).flatMap(([oppName, oppItems]) =>
                            oppItems.map(item => {
                              const isEditing = editingRow === item.capex_item_id;
                              return (
                                <tr key={item.capex_item_id} className="border-t border-gray-100 hover:bg-gray-50">
                                  <td className="px-3 py-2 font-medium text-gray-800">{oppName}</td>
                                  <td className="px-3 py-2 text-gray-700">{item.capex_item_name}</td>
                                  <td className="px-3 py-2 text-right">
                                    {isEditing ? <input type="number" value={editData.unit_cost_low || ''} onChange={e => setEditData(d => ({...d, unit_cost_low: parseFloat(e.target.value) || 0}))} className="w-24 px-2 py-1 border rounded text-right text-sm" />
                                    : (item.unit_cost_low?.toLocaleString() || '-')}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {isEditing ? <input type="number" value={editData.unit_cost_high || ''} onChange={e => setEditData(d => ({...d, unit_cost_high: parseFloat(e.target.value) || 0}))} className="w-24 px-2 py-1 border rounded text-right text-sm" />
                                    : (item.unit_cost_high?.toLocaleString() || '-')}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {isEditing ? <input type="number" value={editData.lifecycle_years || ''} onChange={e => setEditData(d => ({...d, lifecycle_years: parseInt(e.target.value) || 10}))} className="w-16 px-2 py-1 border rounded text-center text-sm" />
                                    : (item.lifecycle_years || '-')}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {item.is_user_modified
                                      ? <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">Yes</span>
                                      : <span className="text-xs text-gray-400">No</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {isEditing ? (
                                      <div className="flex gap-1 justify-center">
                                        <button onClick={() => handleSave(item)} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded"><Save className="w-4 h-4" /></button>
                                        <button onClick={() => setEditingRow(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                                      </div>
                                    ) : (
                                      <div className="flex gap-1 justify-center">
                                        <button onClick={() => { setEditingRow(item.capex_item_id); setEditData(item); }} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                                        <button onClick={() => handleDelete(item.capex_item_id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Row Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New CAPEX Row</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SEU Type <span className="text-red-500">*</span></label>
                <select value={newItem.seu_type} onChange={e => { setNewItem(n => ({...n, seu_type: e.target.value})); setAddErrors(e2 => ({...e2, seu_type: null})); }}
                  className={`w-full px-3 py-2 border rounded-lg ${addErrors.seu_type ? 'border-red-500' : 'border-gray-300'}`}>
                  <option value="">Select SEU Type</option>
                  <option value="A. ELECTRICAL — MOTORS, DRIVES, DISTRIBUTION">A. ELECTRICAL — MOTORS, DRIVES, DISTRIBUTION</option>
                  <option value="B. THERMAL / FUEL">B. THERMAL / FUEL</option>
                  <option value="C. COOLING & HVAC">C. COOLING & HVAC</option>
                  <option value="D. WATER & EFFLUENT">D. WATER & EFFLUENT</option>
                  <option value="E. WASTE">E. WASTE</option>
                  <option value="F. PROCESS & DIGITAL">F. PROCESS & DIGITAL</option>
                </select>
                {addErrors.seu_type && <p className="text-xs text-red-500 mt-1">{addErrors.seu_type}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opportunity Name <span className="text-red-500">*</span></label>
                <input type="text" value={newItem.opportunity_name} onChange={e => { setNewItem(n => ({...n, opportunity_name: e.target.value})); setAddErrors(e2 => ({...e2, opportunity_name: null})); }}
                  placeholder="e.g., A1. Motors / Drives"
                  className={`w-full px-3 py-2 border rounded-lg ${addErrors.opportunity_name ? 'border-red-500' : 'border-gray-300'}`} />
                {addErrors.opportunity_name && <p className="text-xs text-red-500 mt-1">{addErrors.opportunity_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CAPEX Item Name <span className="text-red-500">*</span></label>
                <input type="text" value={newItem.capex_item_name} onChange={e => { setNewItem(n => ({...n, capex_item_name: e.target.value})); setAddErrors(e2 => ({...e2, capex_item_name: null})); }}
                  placeholder="e.g., IE5 Motor"
                  className={`w-full px-3 py-2 border rounded-lg ${addErrors.capex_item_name ? 'border-red-500' : 'border-gray-300'}`} />
                {addErrors.capex_item_name && <p className="text-xs text-red-500 mt-1">{addErrors.capex_item_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technical Specification</label>
                <input type="text" value={newItem.technical_spec} onChange={e => setNewItem(n => ({...n, technical_spec: e.target.value}))}
                  placeholder="e.g., IE5, TEFC, S1 duty" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity Basis</label>
                  <input type="text" value={newItem.capacity_basis} onChange={e => setNewItem(n => ({...n, capacity_basis: e.target.value}))}
                    placeholder="kW" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={newItem.currency} onChange={e => setNewItem(n => ({...n, currency: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {['INR','SGD','USD','AED','SAR','MYR','THB','IDR','VND','CNY','JPY'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost Low <span className="text-red-500">*</span></label>
                  <input type="number" min="0" value={newItem.unit_cost_low} onChange={e => { setNewItem(n => ({...n, unit_cost_low: parseFloat(e.target.value) || 0})); setAddErrors(e2 => ({...e2, unit_cost_low: null})); }}
                    className={`w-full px-3 py-2 border rounded-lg ${addErrors.unit_cost_low ? 'border-red-500' : 'border-gray-300'}`} />
                  {addErrors.unit_cost_low && <p className="text-xs text-red-500 mt-1">{addErrors.unit_cost_low}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost High <span className="text-red-500">*</span></label>
                  <input type="number" min="0" value={newItem.unit_cost_high} onChange={e => { setNewItem(n => ({...n, unit_cost_high: parseFloat(e.target.value) || 0})); setAddErrors(e2 => ({...e2, unit_cost_high: null})); }}
                    className={`w-full px-3 py-2 border rounded-lg ${addErrors.unit_cost_high ? 'border-red-500' : 'border-gray-300'}`} />
                  {addErrors.unit_cost_high && <p className="text-xs text-red-500 mt-1">{addErrors.unit_cost_high}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typical Makes (comma-separated)</label>
                <input type="text" value={newItem.typical_makes} onChange={e => setNewItem(n => ({...n, typical_makes: e.target.value}))}
                  placeholder="ABB, Siemens, CG" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle (years) <span className="text-red-500">*</span></label>
                <input type="number" min="1" value={newItem.lifecycle_years} onChange={e => { setNewItem(n => ({...n, lifecycle_years: parseInt(e.target.value) || 10})); setAddErrors(e2 => ({...e2, lifecycle_years: null})); }}
                  className={`w-full px-3 py-2 border rounded-lg ${addErrors.lifecycle_years ? 'border-red-500' : 'border-gray-300'}`} />
                {addErrors.lifecycle_years && <p className="text-xs text-red-500 mt-1">{addErrors.lifecycle_years}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowAddModal(false); setAddErrors({}); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
              <button onClick={handleAdd} disabled={saving}
                className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#144a78] disabled:opacity-50">
                {saving ? 'Adding...' : 'Add Row'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteMasterDataset;
